<template>
  <div v-if="fieldData && fieldData.values" class="field-input-radio form-group">
    <label
      v-html="fieldData.label"
      :for="fieldData.field_name"
      :class="(isRequired ? 'field-required' : '')">
    </label>

    <div class="input-group">
      <div class="btn-group btn-group-toggle smaller-font-content" data-toggle="buttons">
        <v-select
          :name="'typeahead_' + fieldData.field_name"
          :id="'typeahead_' + fieldData.field_name"
          :aria-describedby="`${fieldData.field_name}_describedby`"
          :ref="fieldData.field_name"
          :disabled="!!(showDisabled())"
          v-bind:class="{ 'std-width form-control': false, 'error': errors.has(fieldData.field_name) }"
          v-model="selectChosen"
          :options="selectOptions"
          :placeholder="placeholderText"
          size="auto" />

        <i v-bind:class="{ 'warning fas fa-exclamation-triangle': errors.has(fieldData.field_name) }"></i>

        <input
          v-show="false"
          :name="fieldData.field_name"
          :id="fieldData.field_name"
          :disabled="!!(showDisabled())"
          type="text"
          class="form-control"
          :maxlength="fieldData.max_length"
          v-model="fieldData.field_value"
          v-validate="fieldData.rules">
      </div>
    </div>
    <div class="invalid-feedback d-block">{{ errors.first(fieldData.field_name) }}</div>
  </div>
</template>

<script>
import vSelect from 'vue-select';

export default {
  inject: ['$validator'], // Allows this component's validation messages to appear in other components
  props: ['fieldData'],
  data() {
    return {
      placeholderDefault: 'Select One', // TODO: Use config value
    };
  },
  components: {
    vSelect,
  },
  methods: {
    showDisabled() {
      return !this.fieldData.is_enabled;
    },
    // TODO: Abstract into helper class
    /**
     * The validation message overlaps the interactive form element, so it is cleared
     * once the components containing the element is clicked.
     */
    clearMessages() {
      this.errors.remove(this.fieldData.field_name); // Temporarily hide the validation message
    },
  },
  computed: {
    isRequired() {
      return this.fieldData.rules && this.fieldData.rules.required === true;
    },
    placeholderText() {
      return this.fieldData.placeholder || this.placeholderDefault;
    },
    selectOptions() {
      /**
      Manually add the placeholder option instead of only using Vue Select's `:placeholder` prop,
      because that one disappears once an option is selected which doesn't allow the empty option
      to be selected again.
      */
      const optionsList = [{
        label: this.placeholderText,
        value: '',
      }];

      return optionsList.concat(this.fieldData.values);
    },
    selectChosen: {
      get() {
        let optionChosen = {
          label: '',
          value: '',
        };

        this.selectOptions.forEach((option) => {
          // Select the option that matches the fieldData's `field_value`
          if (option.value === this.fieldData.field_value) {
            optionChosen = option;
          }
        });

        return (optionChosen.value !== '' ? optionChosen : false);
      },
      set(value = null) {
        let newValue = value;

        if (!newValue) {
          newValue = {
            label: '',
            value: '',
          };
        }

        this.fieldData.field_value = newValue.value;
      },
    },
  },
  watch: {
    'fieldData.field_value': function fieldValue() {
      this.clearMessages();
    },
  },
};
</script>

<style lang="scss">
.selected-tag{
  max-width: 200px !important;
  overflow: hidden !important;
  white-space: nowrap !important;
}
.v-select .vs__actions .clear,
.v-select .dropdown-toggle::after {
  display: none !important;
}
.v-select .dropdown-toggle .clear { visibility: hidden !important; }

.v-select .vs__actions .clear,
.v-select .dropdown-toggle .clear::after {
  display: none !important;
}
.v-select .vs__actions .clear span{
  display: none !important;
}
.v-select .dropdown-toggle .clear { visibility: hidden !important; }
.disabled-field,
.disabled-field label,
.disabled-field input:disabled,
.disabled-field select:disabled,
.disabled-field .btn,
.disabled-field label .star {
  pointer-events: none !important;
  color: #999999 !important;
  cursor: not-allowed !important;
}
.v-select .dropdown-menu .active > a {
  color: #333;
  background:$var-input-icon-background !important;
}
.v-select .dropdown-menu > .highlight > a {
  /*
    * required to override bootstrap 3's
    * .dropdown-menu > li > a:hover {} styles
    */
  background: $var-qs-dark-blue !important;
  color:$var-color-qs-white;
}
.hidden-field {
  display: none;
}

/**V-SELECT: STYLING OVERRIDES**/
.dropdown {
  display: inline-block;
}
.v-select .dropdown-toggle {
  background: #FFFFFF;
  border: 1px solid #CCCCCC;
}
.v-select.disabled .dropdown-toggle{
  border: 1px solid #ccc !important;
  background-color: #ededed !important;
  color: #646464 !important;
}
.v-select.disabled .dropdown-toggle .selected-tag {
  background-color: #ededed !important;
  color: #646464 !important;
}
.v-select.disabled .dropdown-toggle,
.v-select.disabled .dropdown-toggle input,
.v-select.disabled .dropdown-toggle .selected-tag {
  pointer-events: none !important;
  color: #646464 !important;
  cursor: not-allowed !important;
  background-color: #EEEEEE;
}
.form-control::placeholder {
  color: #999999 !important;
}
.v-select .vs__selected-options {
  position: relative;
  width: auto !important;
  height: 21px;
  min-width: 100px;
  padding: 0 10px;
  flex-basis: initial;
  flex-grow: initial;
  flex-wrap: initial;
  flex: 1 0 auto;
  -ms-flex: 1 0 auto;
}

/**Input**/
.v-select input[type=search],
.v-select input[type=search]:focus {
  height: auto;
  padding: 0;
  border: none;
}
.v-select.open input[type=search] {
  position: absolute;
  width: 100% !important;
}

/**Selected Value**/
.v-select .selected-tag {
  position: relative !important;
  margin: 4px 0 0;
  padding: 0;
}

/**Toggle Arrow Icon**/
.v-select .vs__actions {
  padding: 0 5px;
}
.v-select .open-indicator{
  width: 20px;
  background: none !important;
}
.v-select .open-indicator:before {
  margin: 0 auto;
}
.v-select.open .open-indicator:before {
  margin: 10px auto 0;
}

/**Dropdown Options**/
.dropdown-menu {
  width: auto !important;
  padding: 0 0 0.25rem !important;
  overflow-x: hidden;
  overflow-y: auto !important;
  font-size: 0.75rem;
  border-left: 1px solid #CCCCCC !important;
  border-right: 1px solid #CCCCCC !important;
  border-bottom: 1px solid #CCCCCC !important;
  border-top: none !important;
}
.dropdown-menu li>a {
  padding: 3px 20px 3px 10px !important;
}
</style>

<style scoped>
/*
  TODO: Add IE/Edge support by implementing the `label.btn` styles via JavaScript. Bootstrap adds classes
  'focus' and 'active' to the radio <input>'s "closest" element, which happens to be the <label>.
  See https://github.com/twbs/bootstrap/blob/7c7a1706c348fcb5290a63d9249e0c8e4b6ebbcf/js/src/button.js#L153 .
*/
label.btn:focus-within {
  color: #fff;
  background-color: #001a33;
  border-color: #001326;
  z-index: 1;
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
}

select.form-control {
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/admin/interactive/InputSelect.vue?6e550ce2